import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Button from '@material-ui/core/Button';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import SkeletonOverlay from 'Components/UI/SkeletonOverlay';
import { Typography } from '@material-ui/core';
import MeetingBookedLayout from 'Components/Modals/ClientSchedulerModal/Default/MeetingBooked/MeetingBookedLayout';
import { ClientSchedulerMeetingBooked } from 'Overrides/ClientSchedulerMeetingBooked';
import MeetingCancelConfirmationDialog
  from 'Components/Modals/ClientSchedulerModal/Default/MeetingBooked/MeetingCancelConfirmationDialog';
import {
  closeSchedulerModal,
  fetchDataFromLinkQueryParam,
  setNewLinkQueryParam, useClientSchedulerDispatchContext,
  useClientSchedulerStateContext,
} from 'Context/NewClientSchedulerContext';
import { historyWithForceRefresh } from 'State/history';
import { dashboardUri } from 'Consts/clientUris';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import useMobileLayout from 'Hooks/useMobileLayout';
import { removeQuery } from 'Helpers/Query';
import fr from 'facet-rest';
import moment from 'moment-timezone';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: 400,
    alignSelf: 'center',
    padding: theme.spacing(4),
  },
  metaFormRoot: {
    maxWidth: 400,
  },
  title: {
    color: theme.palette.facet.primaryBlue,
    fontWeight: theme.typography.fontWeightBold,
  },
  returnToDashboardButton: {
    fontWeight: theme.typography.fontWeightBold,
    borderRadius: theme.radius.xl,
    flex: 1,
    width: '100%',
    padding: theme.spacing(2),
  },
  cancelBookingButton: {
    fontWeight: theme.typography.fontWeightBold,
    borderRadius: theme.radius.xl,
    color: theme.palette.facet.primaryGray,
    flex: 1,
    width: '100%',
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
});

const MeetingBooked = ({
  classes,
  className,
}) => {
  const { currentCollection } = useClientSchedulerStateContext();
  const dispatch = useClientSchedulerDispatchContext();
  const { logEvent, rootNodeProps } = useEventTrackingLogEvent();
  const mobileView = useMobileLayout();
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const calendarEventResource = currentCollection.resources().head();

  const meetingBookedCalendarEventResource = calendarEventResource
    .overrideMeta(ClientSchedulerMeetingBooked);

  const handleReturnToDashboard = () => {
    closeSchedulerModal();
    historyWithForceRefresh.push(dashboardUri);
    logEvent('Clicked Return to Dashboard', {
      mobileView,
    });
  };

  const handleCancelEvent = () => {
    calendarEventResource.delete();
    handleReturnToDashboard();
    setConfirmationModalOpen(false);
  };

  const handleRescheduleEvent = async () => {
    // fetch meeting resource for calendar event with timezone query parameter
    const [meetingResource] = await calendarEventResource.relationship(fr.relationships.meetings.name).link('related').get({
      params: {
        tz: moment.tz.guess(),
      },
    });

    // set reschedule availability period affordance in link
    setNewLinkQueryParam(dispatch, meetingResource.relationship(fr.relationships.rescheduleMeeting.name).link('related').url());

    // fetch data from link and remove the meeting booked query param
    fetchDataFromLinkQueryParam(dispatch);
    removeQuery('meetingBooked');
  };

  return (
    <div className={classNames(classes.root, className)} {...rootNodeProps}>
      <Typography variant="h2" className={classes.title}>
        Meeting Booked!
      </Typography>
      <SkeletonOverlay loading={meetingBookedCalendarEventResource}>
        <MetaFormInline
          classes={{
            root: classes.metaFormRoot,
          }}
          resource={meetingBookedCalendarEventResource}
          layout={MeetingBookedLayout}
          noIndividualSubmitButtons
        />
      </SkeletonOverlay>
      <Button
        color="primary"
        className={classes.returnToDashboardButton}
        variant="contained"
        disableElevation
        onClick={handleReturnToDashboard}
        test="client-scheduler-return-to-dashboard"
      >
        {'Go to Dashboard'}
      </Button>
      <Button
        className={classes.cancelBookingButton}
        variant="contained"
        disableElevation
        onClick={setConfirmationModalOpen}
        test="client-scheduler-cancel-booking"
      >
        Cancel Booking
      </Button>
      {confirmationModalOpen && (
      <MeetingCancelConfirmationDialog
        title={'Confirmation'}
        message={'Are you sure you want to cancel?'}
        onConfirm={handleCancelEvent}
        cancelLabel={'No, thanks'}
        onCancel={() => {
          setConfirmationModalOpen(false);
        }}
        rescheduleLabel={'Pick a new time'}
        onReschedule={handleRescheduleEvent}
        showCloseIcon
      />
      )}
    </div>
  );
};

MeetingBooked.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
};

MeetingBooked.defaultProps = {
  className: '',
};

export default withStyles(styles)(MeetingBooked);
